import { createRng, type Rng } from '../rng';
import { POWERUPS, powerIsFree, powerupById, type PowerupClass, type TrickTier } from '../powerups';
import { CHARMS, charmById, type CharmTier } from '../charms/catalog';
import { grantCharm } from '../charms/stake';
import { addTrickStock, trickUnlocked } from './trickStock';
import { pushLedger } from './ledger';
import type { Profile } from './types';

/** Until this trophy mark the log refills every 8 hours. After that, every 24. */
export const LOG_FAST_UNTIL = 450;
export const LOG_FAST_MS = 8 * 60 * 60 * 1000;
export const LOG_SLOW_MS = 24 * 60 * 60 * 1000;
export const LOG_AD_MS = 60 * 60 * 1000;
export const LOG_AD_CAP = 2;

const TIER_RANK: Record<CharmTier, number> = {
  twig: 0,
  copper: 1,
  silver: 2,
  gold: 3,
  heartwood: 4,
  starlit: 5,
};

export function emptyLog(): Profile['log'] {
  return { readyAt: 0, adCuts: 0, adDay: '', stickers: 0 };
}

export function logInterval(best: number): number {
  return best < LOG_FAST_UNTIL ? LOG_FAST_MS : LOG_SLOW_MS;
}

export function logReady(profile: Profile, now: number): boolean {
  return now >= (profile.log?.readyAt ?? 0);
}

export function logRemaining(profile: Profile, now: number): number {
  return Math.max(0, (profile.log?.readyAt ?? 0) - now);
}

function dayKey(now: number): string {
  const date = new Date(now);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Offline stand-in for the rewarded ad. Two cuts a day, one hour each. */
export function shortenLog(profile: Profile, now: number): { ok: boolean; reason?: 'cap' | 'ready' } {
  if (!profile.log) profile.log = emptyLog();
  if (logReady(profile, now)) return { ok: false, reason: 'ready' };
  const day = dayKey(now);
  if (profile.log.adDay !== day) {
    profile.log.adDay = day;
    profile.log.adCuts = 0;
  }
  if (profile.log.adCuts >= LOG_AD_CAP) return { ok: false, reason: 'cap' };
  profile.log.adCuts += 1;
  profile.log.readyAt = Math.max(now, profile.log.readyAt - LOG_AD_MS);
  return { ok: true };
}

export interface LogPrize {
  acorns: number;
  charmId: string | null;
  tricks: string[];
  stickers: number;
}

/** Inclusive acorn roll. `40 + int(61)` is 40 through 100. */
export const LOG_ACORN_MIN = 40;
export const LOG_ACORN_MAX = 100;
/** Best unlocked tier, the tier under it, then Copper. These sum to 100. */
export const LOG_CHARM_BEST = 15;
export const LOG_CHARM_BELOW = 35;
export const LOG_CHARM_COPPER = 50;
/** Sprout and Bramble share the low band. These sum to 100. */
export const LOG_TRICK_LOW = 70;
export const LOG_TRICK_OAK = 25;
export const LOG_TRICK_MOONLIT = 5;
export const LOG_TRICK_COUNT = 3;
/** Percent chance of a sticker pack, and how many stickers are in it. */
export const LOG_STICKER_PCT = 30;
export const LOG_STICKER_PACK = 3;

/** What every open can hold. Shown on the Prize Box, without the tier odds. */
export function prizeBoxSummaryLines(): readonly string[] {
  return [
    `${LOG_ACORN_MIN} to ${LOG_ACORN_MAX} acorns`,
    "1 Charm you've unlocked",
    `${LOG_TRICK_COUNT} Tricks for your critter`,
    'Sometimes a sticker pack (album coming soon)',
  ];
}

/** Tier odds for the Odds plaque. Reads the same weights the roll uses. */
export function prizeBoxOddsLines(): readonly string[] {
  return [
    `Charm: Copper ${LOG_CHARM_COPPER}%, one tier below your best ${LOG_CHARM_BELOW}%, your best tier ${LOG_CHARM_BEST}%`,
    `Tricks: Sprout or Bramble ${LOG_TRICK_LOW}%, Oak ${LOG_TRICK_OAK}%, Moonlit ${LOG_TRICK_MOONLIT}%`,
    `Sticker pack: ${LOG_STICKER_PCT}% per open`,
  ];
}

/** One reward per line, in the order an open grants them. Stickers only when a pack dropped. */
export function prizeBoxRewardLines(prize: LogPrize): string[] {
  const lines = [`${prize.acorns} acorns`];
  const charm = prize.charmId ? charmById(prize.charmId)?.name : null;
  if (charm) lines.push(charm);
  const tricks = prize.tricks.map((id) => powerupById(id)?.name ?? id).filter((name) => name.length > 0);
  if (tricks.length > 0) lines.push(tricks.join(', '));
  if (prize.stickers > 0) lines.push(`${prize.stickers} stickers`);
  return lines;
}

function roll(rng: Rng, max: number): number {
  return rng.int(max);
}

function unlockedCharms(profile: Profile): { id: string; tier: CharmTier }[] {
  const out: { id: string; tier: CharmTier }[] = [];
  for (const charm of CHARMS) {
    if (charm.tier === 'twig') continue;
    if (profile.bestTrophies < charm.unlockAt) continue;
    out.push({ id: charm.id, tier: charm.tier });
  }
  return out;
}

function highestTier(charms: readonly { tier: CharmTier }[]): CharmTier {
  let best: CharmTier = 'copper';
  for (const charm of charms) {
    if (TIER_RANK[charm.tier] > TIER_RANK[best]) best = charm.tier;
  }
  return best;
}

function tierBelow(tier: CharmTier): CharmTier {
  const order: CharmTier[] = ['copper', 'silver', 'gold', 'heartwood', 'starlit'];
  const index = order.indexOf(tier);
  return order[Math.max(0, index - 1)] ?? 'copper';
}

function pickCharm(profile: Profile, rng: Rng): string | null {
  const owned = unlockedCharms(profile);
  if (owned.length === 0) return null;
  const high = highestTier(owned);
  const below = tierBelow(high);
  const rollTier = roll(rng, 100);
  const wanted: CharmTier =
    rollTier < LOG_CHARM_BEST ? high : rollTier < LOG_CHARM_BEST + LOG_CHARM_BELOW ? below : 'copper';
  const pool = owned.filter((charm) => charm.tier === wanted);
  const choice = (pool.length > 0 ? pool : owned)[roll(rng, pool.length > 0 ? pool.length : owned.length)];
  return choice?.id ?? null;
}

function heroClasses(profile: Profile): Set<PowerupClass> {
  const classes = new Set<PowerupClass>(['universal']);
  for (const [id, row] of Object.entries(profile.critters)) {
    if (!row?.unlocked) continue;
    const hero = HEROES_SAFE(id);
    if (hero) classes.add(hero);
  }
  return classes;
}

function HEROES_SAFE(id: string): PowerupClass | null {
  const table: Record<string, PowerupClass> = {
    pip: 'guardian',
    bruno: 'guardian',
    tuck: 'guardian',
    bramble: 'infiltration',
    thistle: 'infiltration',
    marlow: 'infiltration',
    cleo: 'stealth',
    wren: 'stealth',
    juniper: 'stealth',
    otto: 'tidecaller',
    lumi: 'tidecaller',
    barley: 'tidecaller',
    fern: 'grove',
    posy: 'grove',
    dash: 'grove',
  };
  return table[id] ?? null;
}

function trickBand(tier: TrickTier): 'low' | 'oak' | 'moonlit' {
  if (tier === 'oak') return 'oak';
  if (tier === 'moonlit') return 'moonlit';
  return 'low';
}

function pickTricks(profile: Profile, rng: Rng): string[] {
  const classes = heroClasses(profile);
  const pool = POWERUPS.filter((power) => !powerIsFree(power) && classes.has(power.classId) && trickUnlocked(profile, power.id));
  const picked: string[] = [];
  for (let n = 0; n < LOG_TRICK_COUNT; n++) {
    const bandRoll = roll(rng, 100);
    const band = bandRoll < LOG_TRICK_LOW ? 'low' : bandRoll < LOG_TRICK_LOW + LOG_TRICK_OAK ? 'oak' : 'moonlit';
    let choices = pool.filter((power) => trickBand(power.tier) === band && !picked.includes(power.id));
    if (choices.length === 0) choices = pool.filter((power) => !picked.includes(power.id));
    if (choices.length === 0) break;
    picked.push(choices[roll(rng, choices.length)].id);
  }
  return picked;
}

export function rollHollowLog(profile: Profile, rng: Rng): LogPrize {
  const acorns = LOG_ACORN_MIN + roll(rng, LOG_ACORN_MAX - LOG_ACORN_MIN + 1);
  const charmId = pickCharm(profile, rng);
  const tricks = pickTricks(profile, rng);
  const stickers = roll(rng, 100) < LOG_STICKER_PCT ? LOG_STICKER_PACK : 0;
  return { acorns, charmId, tricks, stickers };
}

/** Open the log when it is ready. Returns null while the timer is running. */
export function openHollowLog(profile: Profile, now: number, seed: number): LogPrize | null {
  if (!profile.log) profile.log = emptyLog();
  if (!logReady(profile, now)) return null;
  const prize = rollHollowLog(profile, createRng(seed >>> 0));
  profile.wallet.acorns += prize.acorns;
  if (prize.charmId && charmById(prize.charmId)) grantCharm(profile, prize.charmId, 1);
  for (const id of prize.tricks) {
    if (powerupById(id)) addTrickStock(profile, id, 1);
  }
  profile.log.stickers += prize.stickers;
  profile.log.readyAt = now + logInterval(profile.bestTrophies);
  pushLedger(profile, now, 'log', { acorns: prize.acorns }, 0, prize.charmId ?? 'acorns');
  return prize;
}
